"use client";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks";
import { PanelRight, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppSidebar } from "./app-sidebar";

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("ai-chat-sidebar-open");
      return stored ? JSON.parse(stored) : true;
    }
    return true;
  });

  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const isDashboard = type === "dashboard";

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("ai-chat-sidebar-open", JSON.stringify(sidebarOpen));
    }
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex h-full bg-white">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col transition-all duration-300 ease-in-out">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-4 border-b bg-white transition-all duration-300 ease-in-out">
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleSidebar}
                    className="h-8 w-8"
                  >
                    <PanelRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {sidebarOpen ? "Hide Sidebar" : "Show Sidebar"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="flex items-center gap-2">
            {!sidebarOpen && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(isDashboard ? "/chat?type=dashboard" : "/chat")
                      }
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      <span className="hidden sm:inline">
                        {isDashboard ? "New Dashboard AI Chat" : "New Chat"}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isDashboard ? "New Dashboard AI Chat" : "New Chat"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </header>

        {/* Chat Content */}
        <main className="flex-1 flex flex-col transition-all duration-300 ease-in-out min-h-0">
          {children}
        </main>
      </div>

      {/* App Sidebar */}
      <AppSidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
    </div>
  );
}
